package fr.manyverse.app.presentation.viewmodel.gamemaster

import fr.manyverse.app.domain.model.DieConfig
import fr.manyverse.app.domain.model.EventContent
import fr.manyverse.app.domain.model.EventType
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.usecase.GetTableEventsUseCase
import fr.manyverse.app.infrastructure.datastore.UserPreferencesDataStore
import fr.manyverse.app.presentation.components.common_tabs.conversationKey
import fr.manyverse.app.presentation.components.notification.NotificationContent
import kotlin.time.Clock
import kotlin.time.Instant
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val INITIAL_NOTIFICATIONS_LIMIT = 20
private val NOTIFICATION_EVENT_TYPES = listOf(
    EventType.DICE_ROLLED,
    EventType.MESSAGE_SENT,
    EventType.PLAYER_JOINED
)

/**
 * Owns the GameMaster transient notifications (dice rolls, messages, joins).
 * Extracted from GameMasterViewModel; behavior is intentionally identical.
 */
class GameMasterNotificationsDelegate(
    private val scope: CoroutineScope,
    private val tableId: String,
    private val currentUserId: String,
    private val availableDice: List<DieConfig>,
    private val getTableEventsUseCase: GetTableEventsUseCase,
    private val userPreferencesDataStore: UserPreferencesDataStore,
    private val persistLastSeen: (Instant) -> Unit
) {
    private val _notifications = MutableStateFlow<List<NotificationContent>>(emptyList())
    val notifications: StateFlow<List<NotificationContent>> = _notifications.asStateFlow()

    fun loadInitialNotifications() {
        scope.launch {
            val now = Clock.System.now()
            val lastSeenMillis = userPreferencesDataStore.getLastSeenAt(currentUserId, tableId)
            // Premier lancement de cette table pour cet utilisateur : pas de référentiel
            // "déjà vu". On ne montre rien et on initialise le timestamp à maintenant.
            if (lastSeenMillis == null) {
                persistLastSeen(now)
                return@launch
            }
            val since = Instant.fromEpochMilliseconds(lastSeenMillis)
            getTableEventsUseCase(
                tableId = tableId,
                limit = INITIAL_NOTIFICATIONS_LIMIT,
                offset = 0,
                eventTypes = NOTIFICATION_EVENT_TYPES,
                since = since
            ).onSuccess { events ->
                val incoming = events
                    .asSequence()
                    .filter { it.triggeredBy != currentUserId }
                    .mapNotNull(::eventToNotification)
                    .toList()
                if (incoming.isNotEmpty()) {
                    _notifications.update { current ->
                        val existingIds = current.mapTo(HashSet()) { it.id }
                        current + incoming.filter { it.id !in existingIds }
                    }
                }
                persistLastSeen(now)
            }
        }
    }

    /** Adds a notification for a Realtime event when it is relevant to the GM. */
    fun onRealtimeEvent(event: GameEvent) {
        when (event.eventType) {
            EventType.DICE_ROLLED -> {
                if (event.triggeredBy != currentUserId) {
                    val content = event.content as? EventContent.DiceRolled ?: return
                    val dieConfig = availableDice.find { it.type.name == content.dieType }
                    _notifications.update {
                        it + NotificationContent.DiceRoll(
                            id = event.id,
                            characterName = content.characterName,
                            dieType = content.dieType,
                            count = content.count,
                            results = content.results,
                            criticalMin = dieConfig?.criticalMin,
                            criticalMax = dieConfig?.criticalMax,
                            modifier = content.modifier,
                            highValuesWin = dieConfig?.highValuesWin ?: true
                        )
                    }
                }
            }
            EventType.MESSAGE_SENT -> {
                val content = event.content as? EventContent.MessageSent ?: return
                if (event.triggeredBy != currentUserId) {
                    _notifications.update {
                        it + NotificationContent.NewMessage(
                            id = event.id,
                            senderName = content.senderName,
                            text = content.text,
                            conversationKey = event.conversationKey()
                        )
                    }
                }
            }
            EventType.PLAYER_JOINED -> {
                val content = event.content as? EventContent.PlayerJoined ?: return
                _notifications.update {
                    it + NotificationContent.PlayerJoined(
                        id = event.id,
                        characterName = content.characterName
                    )
                }
            }
            else -> Unit
        }
    }

    fun dismissNotification(id: String) {
        _notifications.update { it.filter { n -> n.id != id } }
    }

    fun markAllNotificationsSeen() {
        scope.launch {
            userPreferencesDataStore.saveLastSeenAt(
                userId = currentUserId,
                tableId = tableId,
                epochMillis = Clock.System.now().toEpochMilliseconds()
            )
        }
    }

    private fun eventToNotification(event: GameEvent): NotificationContent? = when (event.eventType) {
        EventType.DICE_ROLLED -> {
            val content = event.content as? EventContent.DiceRolled
            content?.let {
                val dieConfig = availableDice.find { d -> d.type.name == it.dieType }
                NotificationContent.DiceRoll(
                    id = event.id,
                    characterName = it.characterName,
                    dieType = it.dieType,
                    count = it.count,
                    results = it.results,
                    criticalMin = dieConfig?.criticalMin,
                    criticalMax = dieConfig?.criticalMax,
                    modifier = it.modifier,
                    highValuesWin = dieConfig?.highValuesWin ?: true
                )
            }
        }
        EventType.MESSAGE_SENT -> {
            val content = event.content as? EventContent.MessageSent
            content?.let {
                NotificationContent.NewMessage(
                    id = event.id,
                    senderName = it.senderName,
                    text = it.text,
                    conversationKey = event.conversationKey()
                )
            }
        }
        EventType.PLAYER_JOINED -> {
            val content = event.content as? EventContent.PlayerJoined
            content?.let {
                NotificationContent.PlayerJoined(
                    id = event.id,
                    characterName = it.characterName
                )
            }
        }
        else -> null
    }
}
